import { StringUtils } from "../Utils/StringUtils";
import { HashSet } from "../Utils/HashSet";
import { ObjUtils } from "../Utils/ObjUtils";
import { IpVersion, IpAddress } from "../Core/IpAddress";

export class Range {
    min: number;
    max: number;
}

export class IpOctetRange {

    private static MIN_BYTE: number = 0;
    private static MAX_BYTE: number = 255;

    private _MinA: number;
    get MinA(): number {
        return this._MinA;
    }
    set MinA(value: number) {
        this._MinA = value;
    }

    private _MaxA: number;
    get MaxA(): number {
        return this._MaxA;
    }
    set MaxA(value: number) {
        this._MaxA = value;
    }

    private _MinB: number;
    get MinB(): number {
        return this._MinB;
    }
    set MinB(value: number) {
        this._MinB = value;
    }

    private _MaxB: number;
    get MaxB(): number {
        return this._MaxB;
    }
    set MaxB(value: number) {
        this._MaxB = value;
    }

    private _MinC: number;
    get MinC(): number {
        return this._MinC;
    }
    set MinC(value: number) {
        this._MinC = value;
    }

    private _MaxC: number;
    get MaxC(): number {
        return this._MaxC;
    }
    set MaxC(value: number) {
        this._MaxC = value;
    }

    private _MinD: number;
    get MinD(): number {
        return this._MinD;
    }
    set MinD(value: number) {
        this._MinD = value;
    }

    private _MaxD: number;
    get MaxD(): number {
        return this._MaxD;
    }
    set MaxD(value: number) {
        this._MaxD = value;
    }

    private static ParseByte(s: string): any {
        if (/^\+?(0|[1-9]\d*)$/.test(s)) {
            var value = parseInt(s);
            if (!isNaN(value) && (value >= 0) && (value <= 255)) {
                return value;
            }
        }
        return Number.NaN;
    }

    private static ParseDash(rangeStr: string): Range {

        var range: Range = new Range();

        range.min = this.MIN_BYTE;
        range.max = this.MAX_BYTE;

        if (!StringUtils.IsNullOrWhiteSpace(rangeStr)) {
            var b;

            var dashIdx: number = rangeStr.indexOf('-');
            var octetArray: string[] = rangeStr.split('-', 2);
            if (Array.isArray(octetArray) && (octetArray.length === 2)) {
                var minStr: string = octetArray[0].trim();
                var maxStr: string = octetArray[1].trim();

                b = this.ParseByte(minStr);
                if (!isNaN(b))
                    range.min = b;

                b = this.ParseByte(maxStr);
                if (!isNaN(b))
                    range.max = b;

                if (range.min > range.max)
                    range.max = range.min;
            }
            else {
                b = this.ParseByte(rangeStr);
                if (!isNaN(b)) {
                    range.min = b;
                    range.max = b;
                }
            }
        }

        return range;
    }

    public static Create(rangeStr: string): IpOctetRange {
        if (StringUtils.IsNullOrWhiteSpace(rangeStr))
            return null;

        var ipOctetRange: IpOctetRange = null;

        var strA: string = "";
        var strB: string = "";
        var strC: string = "";
        var strD: string = "";

        var octetArray: string[] = rangeStr.split('.');
        if (Array.isArray(octetArray) && (octetArray.length >= 4)) {
            if (!StringUtils.IsNullOrWhiteSpace(octetArray[0]))
                strA = octetArray[0];
            if (!StringUtils.IsNullOrWhiteSpace(octetArray[1]))
                strB = octetArray[1];
            if (!StringUtils.IsNullOrWhiteSpace(octetArray[2]))
                strC = octetArray[2];
            if (!StringUtils.IsNullOrWhiteSpace(octetArray[3]))
                strD = octetArray[3];

            var rangeA: Range = this.ParseDash(strA);
            var rangeB: Range = this.ParseDash(strB);
            var rangeC: Range = this.ParseDash(strC);
            var rangeD: Range = this.ParseDash(strD);

            ipOctetRange = new IpOctetRange(rangeA.min, rangeA.max, rangeB.min, rangeB.max,
                rangeC.min, rangeC.max, rangeD.min, rangeD.max);
        }

        return ipOctetRange;
    }

    constructor(minA?: number, maxA?: number, minB?: number, maxB?: number,
        minC?: number, maxC?: number, minD?: number, maxD?: number) {

        this._Init();

        if (minA) {
            this.MinA = this.MaxA = minA;
            if (maxA > this.MaxA)
                this.MaxA = maxA;

            this.MinB = this.MaxB = minB;
            if (maxB > this.MaxB)
                this.MaxB = maxB;

            this.MinC = this.MaxC = minC;
            if (maxC > this.MaxC)
                this.MaxC = maxC;

            this.MinD = this.MaxD = minD;
            if (maxD > this.MaxD)
                this.MaxD = maxD;
        }
    }

    private _Init(): void {
        this.MinA = this.MaxA = IpOctetRange.MIN_BYTE;
        this.MinB = this.MaxB = IpOctetRange.MIN_BYTE;
        this.MinC = this.MaxC = IpOctetRange.MIN_BYTE;
        this.MinD = this.MaxD = IpOctetRange.MIN_BYTE;
    }

    public Clone(): IpOctetRange {
        var clonedRange: IpOctetRange = new IpOctetRange(this.MinA, this.MaxA,
            this.MinB, this.MaxB, this.MinC, this.MaxC, this.MinD, this.MaxD);
        return clonedRange;
    }

    public Contains(ip: IpAddress): boolean {
        if (!ip)
            return false;
        if (ip.IpVersion != IpVersion.IPv4)
            return false;

        var A: number = ip.Bytes[0];
        var B: number = ip.Bytes[1];
        var C: number = ip.Bytes[2];
        var D: number = ip.Bytes[3];

        if ((A < this.MinA) || (A > this.MaxA))
            return false;
        if ((B < this.MinB) || (B > this.MaxB))
            return false;
        if ((C < this.MinC) || (C > this.MaxC))
            return false;
        if ((D < this.MinD) || (D > this.MaxD))
            return false;

        return true;
    }

    public toString(): string {
        var strA: string = "";
        var strB: string = "";
        var strC: string = "";
        var strD: string = "";

        if (this.MinA == this.MaxA)
            strA = this.MinA.toString();
        else
            strA = this.MinA.toString() + "-" + this.MaxA.toString();

        if (this.MinB == this.MaxB)
            strB = this.MinB.toString();
        else
            strB = this.MinB.toString() + "-" + this.MaxB.toString();

        if (this.MinC == this.MaxC)
            strC = this.MinC.toString();
        else
            strC = this.MinC.toString() + "-" + this.MaxC.toString();

        if (this.MinD == this.MaxD)
            strD = this.MinD.toString();
        else
            strD = this.MinD.toString() + "-" + this.MaxD.toString();

        var rangeStr: string = strA + "." + strB + "." + strC + "." + strD;
        return rangeStr;
    }

	public ToString(): string {
		return this.toString();
	}

    get IpAddresses(): HashSet<IpAddress> {
        var ipAddresses: HashSet<IpAddress> = new HashSet<IpAddress>();

        var newIp: IpAddress = null;

        var A, B, C, D: number;

        for (A = this.MinA; A <= this.MaxA; A++) {
            for (B = this.MinB; B <= this.MaxB; B++) {
                for (C = this.MinC; C <= this.MaxC; C++) {
                    for (D = this.MinD; D <= this.MaxD; D++) {
                        newIp = IpAddress.CreateFromOctets(A, B, C, D);
                        if (newIp != null) {
                            ipAddresses.Add(newIp);
                        }
                    }
                }
            }
        }

        return (ipAddresses);
    }

    public Equals(obj: any): boolean {
        if (obj == null)
            return false;

        if (obj === this)
            return true;

        if (!(obj instanceof IpOctetRange))
            return false;

        var other: IpOctetRange = <IpOctetRange>obj;
        if (other == null)
            return false;

        if (this.MinA != other.MinA)
            return false;
        if (this.MaxA != other.MaxA)
            return false;

        if (this.MinB != other.MinB)
            return false;
        if (this.MaxB != other.MaxB)
            return false;

        if (this.MinC != other.MinC)
            return false;
        if (this.MaxC != other.MaxC)
            return false;

        if (this.MinD != other.MinD)
            return false;
        if (this.MaxD != other.MaxD)
            return false;

        return true;
    }

    public GetHashCode(): number {
        var rangeStr: string = this.ToString();
        return StringUtils.GetHashCode(rangeStr);
    }

}

 